/* Daps — cart drawer.
 *
 * Owns: opening/closing the drawer, rendering line items from /cart.js,
 * quantity edits, and the zone-aware free-shipping progress + one-tap
 * gap-closing offer.
 *
 * Why the shipping logic lives here as well as in the buy box: the threshold
 * is a property of the ORDER, and closing a gap usually needs a different
 * pack rather than more of the same one. The buy box can only ever speak for
 * the line being configured; this can speak for the whole cart.
 *
 * Currency: thresholds are denominated in the shop currency because that is
 * what the Shopify delivery profile compares against. Markets may show this
 * visitor another currency, so comparisons are done in shop currency via
 * Shopify.currency.rate and displayed back in the visitor's own currency.
 *
 * Never touches the buy box. daps-buy.js calls window.dapsCart.open() and
 * nothing else. No dependencies.
 */
(function () {
  "use strict";

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  var drawer = $("[data-cart-drawer]");
  if (!drawer) return;

  var linesEl = $("[data-cdraw-lines]", drawer);
  var shipEl  = $("[data-cdraw-ship]", drawer);
  var subEl   = $("[data-cdraw-sub]", drawer);

  var busy = false;
  var lastCart = null;

  /* ---- currency ---------------------------------------------------------- */

  function fxRate() {
    try {
      var r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      return (isFinite(r) && r > 0) ? r : 1;
    } catch (e) { return 1; }
  }

  function activeCurrency() {
    try {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
             drawer.getAttribute("data-shop-currency") || "USD";
    } catch (e) { return "USD"; }
  }

  /* presentment cents -> shop-currency units (what the delivery rule sees) */
  function toShopUnits(cents) { return (cents / 100) / fxRate(); }

  /* shop-currency units -> presentment cents, rounded UP so we never
     understate what is still owed */
  function toPresentmentCents(units) { return Math.ceil(units * fxRate() * 100); }

  function money(cents) {
    var iso = activeCurrency();
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency", currency: iso, currencyDisplay: "symbol"
      }).format(cents / 100);
    } catch (e) {
      try { return new Intl.NumberFormat("en", { style: "currency", currency: iso }).format(cents / 100); }
      catch (e2) { return "$" + (cents / 100).toFixed(2); }
    }
  }

  /* ---- pack catalog (for the gap-closing offer) -------------------------- */

  var packs = (function () {
    var node = $(".cdraw-packs", drawer);
    if (!node) return [];
    var raw;
    try { raw = JSON.parse(node.textContent); } catch (e) { return []; }
    return raw.map(function (v) {
      var opts = v.opts || [];
      var pack = null, flavor = null;
      opts.forEach(function (o) {
        if (/strip/i.test(String(o))) pack = o; else if (flavor === null) flavor = o;
      });
      return { id: v.id, pack: pack, flavor: flavor, price: v.price, available: v.available };
    }).filter(function (v) { return v.available && v.pack; });
  })();

  /* The cheapest pack that on its own closes the remaining gap. Null when no
     single pack can — we must not imply otherwise. */
  function packForGap(gapPresentmentCents, preferFlavor) {
    if (!packs.length) return null;
    var pool = packs.filter(function (p) { return !preferFlavor || p.flavor === preferFlavor; });
    if (!pool.length) pool = packs;
    pool = pool.slice().sort(function (a, b) { return a.price - b.price; });
    for (var i = 0; i < pool.length; i++) {
      if (pool[i].price >= gapPresentmentCents) return pool[i];
    }
    return null;
  }

  function largestPack(preferFlavor) {
    var pool = packs.filter(function (p) { return !preferFlavor || p.flavor === preferFlavor; });
    if (!pool.length) pool = packs;
    return pool.slice().sort(function (a, b) { return b.price - a.price; })[0] || null;
  }

  /* ---- open / close ------------------------------------------------------ */

  function open() {
    drawer.classList.add("is-open");
    document.body.style.overflow = "hidden";
    var c = $(".cdraw__close", drawer);
    if (c) c.focus();
    refresh();
  }

  function close() {
    drawer.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  /* ---- rendering -------------------------------------------------------- */

  function lineRow(item) {
    var wrap = document.createElement("div");
    wrap.className = "cdrow";
    wrap.setAttribute("data-key", item.key);

    var img = document.createElement("div");
    img.className = "cdrow__img";
    if (item.image) {
      var i = document.createElement("img");
      i.src = item.image.replace(/(\.[a-z]+)(\?|$)/i, "_160x$1$2");
      i.alt = item.product_title || "";
      i.width = 64; i.height = 64; i.loading = "lazy";
      img.appendChild(i);
    }

    var info = document.createElement("div");
    info.className = "cdrow__info";

    var title = document.createElement("span");
    title.className = "cdrow__title";
    title.textContent = String(item.product_title || "").toLowerCase();
    info.appendChild(title);

    if (item.variant_title && !/default/i.test(item.variant_title)) {
      var meta = document.createElement("span");
      meta.className = "cdrow__meta";
      meta.textContent = item.variant_title;
      info.appendChild(meta);
    }

    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      var plan = document.createElement("span");
      plan.className = "cdrow__plan";
      plan.textContent = item.selling_plan_allocation.selling_plan.name;
      info.appendChild(plan);
    }

    var ctrls = document.createElement("div");
    ctrls.className = "cdrow__ctrls";

    var stepper = document.createElement("div");
    stepper.className = "cdrow__qty";
    var dec = document.createElement("button");
    dec.type = "button"; dec.className = "cdrow__qbtn";
    dec.setAttribute("data-cdraw-qty", String(item.quantity - 1));
    dec.setAttribute("aria-label", "decrease quantity");
    dec.innerHTML = "&minus;";
    var val = document.createElement("span");
    val.className = "cdrow__qval";
    val.textContent = item.quantity;
    var inc = document.createElement("button");
    inc.type = "button"; inc.className = "cdrow__qbtn";
    inc.setAttribute("data-cdraw-qty", String(item.quantity + 1));
    inc.setAttribute("aria-label", "increase quantity");
    inc.textContent = "+";
    stepper.appendChild(dec); stepper.appendChild(val); stepper.appendChild(inc);

    var price = document.createElement("span");
    price.className = "cdrow__price";
    price.textContent = money(item.final_line_price);

    ctrls.appendChild(stepper);
    ctrls.appendChild(price);
    info.appendChild(ctrls);

    var rm = document.createElement("button");
    rm.type = "button"; rm.className = "cdrow__remove";
    rm.setAttribute("data-cdraw-qty", "0");
    rm.textContent = "remove";
    info.appendChild(rm);

    wrap.appendChild(img);
    wrap.appendChild(info);
    return wrap;
  }

  function renderShip(cart) {
    if (!shipEl) return;

    if (drawer.getAttribute("data-intl") !== "1" || !cart.item_count) {
      shipEl.hidden = true;
      shipEl.textContent = "";
      return;
    }

    shipEl.hidden = false;
    shipEl.textContent = "";

    /* No zone = we cannot ship there. Say so, make no shipping claim. */
    if (drawer.getAttribute("data-supported") !== "1") {
      var none = document.createElement("p");
      none.className = "cdraw__ship-label";
      none.textContent = drawer.getAttribute("data-unsupported-line") || "";
      shipEl.appendChild(none);
      return;
    }

    var threshold = parseFloat(drawer.getAttribute("data-threshold"));
    if (!isFinite(threshold) || threshold <= 0) { shipEl.hidden = true; return; }

    var subFree = drawer.getAttribute("data-sub-free") === "1";
    var allSub = cart.items.length > 0 && cart.items.every(function (i) { return !!i.selling_plan_allocation; });

    var label = document.createElement("p");
    label.className = "cdraw__ship-label";

    /* Subscriptions ship free with no minimum in this zone (US): nothing to chase. */
    if (subFree && allSub) {
      label.className += " is-met";
      label.textContent = "✓ your subscription ships free.";
      shipEl.appendChild(label);
      return;
    }

    var haveShopUnits = toShopUnits(cart.total_price);

    if (haveShopUnits >= threshold) {
      label.className += " is-met";
      label.textContent = "✓ this order ships free.";
      shipEl.appendChild(label);
      shipEl.appendChild(track(100));
      return;
    }

    var gapCents = toPresentmentCents(threshold - haveShopUnits);
    label.textContent = "you're " + money(gapCents) + " from free shipping.";
    shipEl.appendChild(label);
    shipEl.appendChild(track(Math.max(2, Math.min(100, Math.round((haveShopUnits / threshold) * 100)))));

    /* One tap to close the gap. Prefer the flavour already in the cart so we
       are not silently switching what they picked. Added as a ONE-TIME line:
       an add-on to reach a threshold must never create a second subscription. */
    var preferFlavor = null;
    for (var i = 0; i < cart.items.length; i++) {
      var vt = cart.items[i].variant_title || "";
      var parts = vt.split(" / ");
      if (parts.length) { preferFlavor = parts[0]; break; }
    }

    /* Shopify evaluates the delivery condition against the DISCOUNTED total,
       so a pack only really closes the gap if its price still does after any
       cart discount. Without this, an active code makes us promise free
       shipping that checkout would not honour. */
    var ratio = (cart.items_subtotal_price > 0) ? (cart.total_price / cart.items_subtotal_price) : 1;
    if (!isFinite(ratio) || ratio <= 0 || ratio > 1) ratio = 1;

    var pick = packForGap(gapCents / ratio, preferFlavor);
    var closes = !!pick;
    if (!pick) pick = largestPack(preferFlavor);
    if (!pick) return;

    /* The add-on goes in as a one-time line, so for a cart that contains a
       subscription it only makes THIS order ship free — renewals contain just
       the subscription. Say "this order" rather than implying it is ongoing. */
    var anySub = cart.items.some(function (i) { return !!i.selling_plan_allocation; });
    var promise = closes ? (anySub ? " — free shipping on this order" : " — unlocks free shipping") : "";

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "cdraw__offer";
    btn.setAttribute("data-cdraw-add", String(pick.id));
    btn.innerHTML = '<span class="cdraw__offer-plus">+</span><span>add a ' + pick.pack + promise + "</span>" +
                    '<span class="cdraw__offer-price">' + money(pick.price) + "</span>";
    shipEl.appendChild(btn);
  }

  function track(pct) {
    var t = document.createElement("div");
    t.className = "cdraw__track";
    var f = document.createElement("span");
    f.className = "cdraw__fill";
    f.style.width = pct + "%";
    t.appendChild(f);
    return t;
  }

  function render(cart) {
    lastCart = cart;
    if (subEl) subEl.textContent = money(cart.total_price);

    if (linesEl) {
      linesEl.textContent = "";
      if (!cart.item_count) {
        var empty = document.createElement("p");
        empty.className = "cdraw__empty";
        empty.textContent = "your cart is empty.";
        linesEl.appendChild(empty);
      } else {
        cart.items.forEach(function (item) { linesEl.appendChild(lineRow(item)); });
      }
    }

    renderShip(cart);
    updateCount(cart.item_count);
    drawer.classList.toggle("is-busy", false);
  }

  function updateCount(n) {
    $$("[data-cart-count]").forEach(function (el) { el.textContent = n; });
  }

  /* ---- cart mutations --------------------------------------------------- */

  function refresh() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(render)
      .catch(function () {});
  }

  function changeQty(key, qty) {
    if (busy) return;
    busy = true;
    drawer.classList.add("is-busy");
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); })
      .then(function (cart) { busy = false; render(cart); })
      .catch(function () { busy = false; drawer.classList.remove("is-busy"); refresh(); });
  }

  function addVariant(id) {
    if (busy) return;
    busy = true;
    drawer.classList.add("is-busy");
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    }).then(function () { busy = false; return refresh(); })
      .catch(function () { busy = false; drawer.classList.remove("is-busy"); refresh(); });
  }

  /* ---- wiring ----------------------------------------------------------- */

  document.addEventListener("click", function (e) {
    if (e.target.closest("[data-cdraw-close]")) { close(); return; }

    var qtyBtn = e.target.closest("[data-cdraw-qty]");
    if (qtyBtn) {
      var row = qtyBtn.closest("[data-key]");
      if (row) changeQty(row.getAttribute("data-key"), parseInt(qtyBtn.getAttribute("data-cdraw-qty"), 10));
      return;
    }

    var addBtn = e.target.closest("[data-cdraw-add]");
    if (addBtn) { addVariant(addBtn.getAttribute("data-cdraw-add")); return; }

    /* header cart link opens the drawer instead of navigating; it stays a real
       link so it still works if this script never runs */
    var cartLink = e.target.closest('a[href$="/cart"], a[href="/cart"], .nav__cart');
    if (cartLink && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      open();
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && drawer.classList.contains("is-open")) close();
  });

  window.dapsCart = { open: open, close: close, refresh: refresh };
})();
